import {useCallback} from 'react';
import {getStore} from 'src/v2/state/store';
import {
  OpenapiCryptocurrencyPayinsResourceConsumerWalletSessionProviderEnum,
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum,
  OpenapiPostV1CryptoInternalSetupSessionCryptoSetupSessionConfirmInputNetworkEnum,
} from '@stripe-internal/data-v1/schema/types';
import useConfirmCryptoSetupMutation from 'src/api/graphql/useConfirmCryptoSetupMutation';

type ConfirmSetupParams = {
  selectedCurrency: OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum;
  walletAddress: string;
  network: OpenapiPostV1CryptoInternalSetupSessionCryptoSetupSessionConfirmInputNetworkEnum;
  walletProvider: OpenapiCryptocurrencyPayinsResourceConsumerWalletSessionProviderEnum;
  walletKey?: string;
  signedTransactionData?: string;
  transactionHash?: string;
};

export const useConfirmSessionForSetup = () => {
  const store = getStore();
  const [confirmCryptoSetup] = useConfirmCryptoSetupMutation();
  const sessionId = store.useSelector((state) => state.id);
  const clientSecret = store.useSelector((state) => state.clientSecret);

  return useCallback(
    async (params: ConfirmSetupParams) => {
      const response = await confirmCryptoSetup({
        variables: {
          input: {
            client_secret: clientSecret,
            crypto_setup_session: sessionId,
            network: params.network,
            currency: params.selectedCurrency,
            wallet_address: params.walletAddress,
            provider: params.walletProvider,
            wallet_key: params.walletKey,
            signed_transaction_data: params.signedTransactionData,
            transaction_hash: params.transactionHash,
          },
        },
      });

      const updatedSession =
        response.data?.v1CryptoInternalSetupSessionCryptoSetupSessionConfirm;

      if (response.error) {
        throw response.error;
      }

      if (!updatedSession) {
        throw new Error('No session returned from confirmCryptoSetup');
      }

      store.setValue((state) => {
        state.state = updatedSession.state;
      });
    },
    [sessionId, clientSecret, confirmCryptoSetup, store],
  );
};
